import { fetchContacts } from '../../contacts/actions';
import {
  GeckoDialogService,
  GeckoModalService,
  GeckoToastService,
  GeckoLoadingService,
} from '../../../ui';
import GeckoLocation from 'services/gecko-location';
import GeckoRequest from 'services/gecko-request';
import CONSTANTS from 'chat-constants';
import { renderError } from 'services/gecko-error';

export const getSubjectLineIfEmail = (conversation, channels) => {
  const channelType = channels.find(
    (channel) => channel.id === conversation.last_message_channel
  ).type;
  const subjectLine = conversation.subject_line;

  if (channelType === 'mailgun' && subjectLine) {
    return subjectLine;
  }

  return null;
};

export const handleDeleteContact = async (
  contactId,
  fetchContactsPage = null,
  shouldResetConversation = false
) => {
  const confirm = await new GeckoDialogService().confirm(
    'Are you sure you want to delete this Contact?'
  );
  if (confirm) {
    if (shouldResetConversation) {
      // get around an socket race condition issue on conversation
      // by reset the url location so that the conversation has nothing loaded.
      await GeckoLocation.goTo('/conversations', { ...GeckoLocation.params });
    }

    try {
      const response = await GeckoRequest.post('/contact/delete', {
        contact_id: contactId,
      });
      const hasDeletedContacts = response.numContacts > 0;
      const shouldFetchContacts =
        fetchContactsPage !== null && hasDeletedContacts;

      if (shouldFetchContacts) {
        fetchContacts({ fetchContactsPage });
      }

      if (hasDeletedContacts) {
        new GeckoToastService().success('Contact delete successful');
        return new GeckoModalService().close();
      }

      new GeckoToastService().error('No contact was deleted');
      return new GeckoModalService().close();
    } catch (error) {
      console.error(error);
    }
  }
};

export const getShouldStartNewConversation = (channel, lastMessageChannel) => {
  const newChannelIsMailgun = channel.type === CONSTANTS.CHANNEL.TYPE_MAILGUN;
  const currentChannelIsNotFormResponse =
    lastMessageChannel?.type !== CONSTANTS.CHANNEL.TYPE_FORM_RESPONSE;
  return currentChannelIsNotFormResponse && newChannelIsMailgun;
};

export const getConversationRichTextTranscript = (conversationId) => {
  return new GeckoLoadingService().resolve(
    GeckoRequest.get(`/conversation/${conversationId}/get-rich-text-transcript`)
  );
};

export const getConversationTranscript = (conversationId) => {
  return new GeckoLoadingService().resolve(
    GeckoRequest.get(`/conversation/${conversationId}/transcript`)
  );
};

export const getMessageText = (editorState) => {
  return editorState.getCurrentContent().getPlainText();
};

export const openStaticModalWith = (Component) => {
  new GeckoModalService().open(() => Component, { backdrop: 'static' });
};

const formatMessageMetadata = (unformattedMessage, conversationId) => {
  const messageMetadata = unformattedMessage.metadata || {};
  const message = {
    ...unformattedMessage,
    metadata: {
      ...messageMetadata,
      forceNewThread: true,
      threaded_from: conversationId,
    },
  };
  return message;
};

export const messageComposeRequest = async (requestOpts) => {
  return new GeckoLoadingService().resolve(
    GeckoRequest.post('/message/compose', requestOpts)
  );
};

const internalNoteRequest = async (options) => {
  return new GeckoLoadingService().resolve(
    GeckoRequest.post(`/conversation/internal-note`, options)
  );
};

const generateEditorObject = (content, url, urlText) => {
  const urlStart = content.indexOf(urlText);
  const urlEnd = urlText.length;
  return {
    blocks: [
      {
        key: 'c1sju',
        data: {},
        text: content,
        type: 'unstyled',
        depth: 0,
        entityRanges: [
          {
            key: 0,
            length: urlEnd,
            offset: urlStart,
          },
        ],
        inlineStyleRanges: [],
      },
    ],
    entityMap: {
      0: {
        data: {
          url: url,
          targetOption: '_blank',
        },
        type: 'LINK',
        mutability: 'MUTABLE',
      },
    },
  };
};

const addNoteForNewThread = async (
  channel,
  senderId,
  contactId,
  currentConversationId,
  newConversationId
) => {
  const content = `Conversation started from ${channel}. Click Here to view the previous conversation thread`;
  const url = `${process.env.REACT_APP_APP_URL}/#/conversations/${currentConversationId}?filter=Contact&contact=${contactId}`;
  const urlText = 'Click Here';

  return internalNoteRequest({
    sender_id: senderId,
    conversation_id: newConversationId,
    content,
    editor_object: generateEditorObject(content, url, urlText),
  });
};

const addNoteForOldThread = async (
  channel,
  senderId,
  contactId,
  currentConversationId,
  newConversationId
) => {
  const content = `Replied via ${channel}. Click Here to view the new conversation thread`;
  const url = `${process.env.REACT_APP_APP_URL}/#/conversations/${newConversationId}?filter=Contact&contact=${contactId}`;
  const urlText = 'Click Here';

  return internalNoteRequest({
    sender_id: senderId,
    conversation_id: currentConversationId,
    content,
    editor_object: generateEditorObject(content, url, urlText),
  });
};

export const showRequestSuccess = () => {
  new GeckoToastService().success('Messages sent');
  new GeckoModalService().close();
};

export const sendMessage = (
  oldChannel,
  newChannel,
  contactId,
  agentId,
  currentConversationId
) => {
  return async (message, toContactIds, selectedChannel, assignTo) => {
    const requestOpts = {
      to: toContactIds,
      channel: selectedChannel,
      assign_to: assignTo,
      message: formatMessageMetadata(message, currentConversationId),
    };

    try {
      const response = await messageComposeRequest(requestOpts);
      const { conversationIds } = response;
      const newConversationId = conversationIds[0];
      const newChannelName = newChannel.name;
      const oldChannelName = oldChannel?.name || 'another channel';

      await addNoteForOldThread(
        newChannelName,
        agentId,
        contactId,
        currentConversationId,
        newConversationId
      );
      await addNoteForNewThread(
        oldChannelName,
        agentId,
        contactId,
        currentConversationId,
        newConversationId
      );

      return showRequestSuccess();
    } catch (error) {
      renderError(error);
    }
  };
};

export const messageForwardRequest = async (requestOpts, conversationId) => {
  return new GeckoLoadingService().resolve(
    GeckoRequest.post(`/conversation/${conversationId}/forward`, requestOpts)
  );
};

// Differences: Different stuff to send, different URL & different callbacks.
export const forwardMessage = (conversationId) => {
  return (message, toContactIds, selectedChannel) => {
    const requestOpts = {
      forwards: toContactIds,
      message: message,
      channel: selectedChannel,
    };

    return messageForwardRequest(requestOpts, conversationId)
      .then(showRequestSuccess)
      .catch(renderError);
  };
};
